import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { licenseService, enumService, tenantService, userService } from '../services/api';
import { GenerateLicenseModal, AssignOwnerModal, RevokeConfirmModal, DeactivateConfirmModal } from '../components/LicenseModals';

const Licenses = () => {
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ search: '', status: 'all', ownerType: '', owner: '' });
  const [sorting, setSorting] = useState({ sortBy: 'created_at', sortOrder: 'desc' });
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  
  // Modals state
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [assignLicense, setAssignLicense] = useState(null);
  const [revokeLicense, setRevokeLicense] = useState(null);
  const [deactivateLicense, setDeactivateLicense] = useState(null);
  
  // Enum options from API
  const [enums, setEnums] = useState({});
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);

  // Fetch enums on mount
  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const response = await enumService.getAll();
        setEnums(response.enums || {});
      } catch (err) {
        console.error('Error fetching enums:', err);
      }
    };
    const fetchTenants = async () => {
      try {
        const response = await tenantService.getAll({ pageSize: 100 });
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };
    const fetchUsers = async () => {
      try {
        const response = await userService.getAll({ pageSize: 100 });
        setUsers(response.data || []);
      } catch (err) {
        console.error('Error fetching users:', err);
      }
    };
    fetchEnums();
    fetchTenants();
    fetchUsers();
  }, []);

  const fetchLicenses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await licenseService.getAll({
        page: pagination.page,
        pageSize: pagination.pageSize,
        search: filters.search,
        status: filters.status !== 'all' ? filters.status : undefined,
        ownerType: filters.ownerType,
        owner: filters.owner,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder,
      });
      
      setLicenses(response.data || []);
      if (response.pagination) {
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (err) {
      console.error('Error fetching licenses:', err);
      setError('Failed to load licenses');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters, sorting]);

  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleSearch = (e) => {
    setFilters({ ...filters, search: e.target.value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handleSort = (field) => {
    setSorting(prev => ({
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return '↕';
    return sorting.sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleRevoke = async (license) => {
    setActionLoading(license.id);
    try {
      await licenseService.revoke(license.id, 'Revoked by admin');
      setMessage(`License "${license.licenseKey}" revoked successfully`);
      setRevokeLicense(null);
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to revoke license');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnrevoke = async (license) => {
    setActionLoading(license.id);
    try {
      await licenseService.unrevoke(license.id);
      setMessage(`License "${license.licenseKey}" unrevoked successfully`);
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unrevoke license');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeactivate = async (license) => {
    setActionLoading(license.id);
    try {
      await licenseService.deactivate(license.id, 'Deactivated by admin');
      setMessage(`License "${license.licenseKey}" deactivated successfully`);
      setDeactivateLicense(null);
      fetchLicenses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate license');
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'unowned': { label: 'Unowned', color: 'status-pending', icon: '🟡' },
      'available': { label: 'Available', color: 'status-active', icon: '🟢' },
      'activated': { label: 'Activated', color: 'status-active', icon: '🟢' },
      'deactivated': { label: 'Deactivated', color: 'status-disabled', icon: '⚪' },
      'revoked': { label: 'Revoked', color: 'status-error', icon: '🔴' },
    };
    const statusInfo = statusMap[status] || { label: status, color: 'status-pending', icon: '🟡' };
    return <span className={`status-badge ${statusInfo.color}`}>{statusInfo.icon} {statusInfo.label}</span>;
  };

  const getOwnerTypeBadge = (ownerType) => {
    const typeMap = {
      'tenant': { label: 'Tenant', icon: '🏢' },
      'user': { label: 'User', icon: '👤' },
      'device': { label: 'Device', icon: '📱' },
    };
    const typeInfo = typeMap[ownerType] || { label: ownerType || '—', icon: '' };
    return ownerType ? <span className="platform-badge">{typeInfo.icon} {typeInfo.label}</span> : <span style={{ color: '#999' }}>—</span>;
  };

  const getOwnerName = (license) => {
    if (!license.ownerType) return <span style={{ color: '#999' }}>Unassigned</span>;
    
    if (license.ownerType === 'tenant' && license.tenantName) {
      return <Link to={`/tenants/${license.ownerId}`}>{license.tenantName}</Link>;
    }
    
    if (license.ownerType === 'user' && license.userName) {
      return <Link to={`/users/${license.ownerId}`}>{license.userName}</Link>;
    }
    
    if (license.ownerType === 'device' && license.deviceName) {
      return <Link to={`/devices/${license.ownerId}`}>{license.deviceName}</Link>;
    }
    
    return <span style={{ color: '#999', fontFamily: 'monospace', fontSize: '0.85rem' }}>{license.ownerId || '—'}</span>;
  };

  const getDeviceLink = (license) => {
    if (license.status === 'activated' && license.activatedDeviceId && license.activatedDeviceThingName) {
      return <Link to={`/devices/${license.activatedDeviceId}`}>{license.activatedDeviceThingName}</Link>;
    }
    return <span style={{ color: '#999' }}>—</span>;
  };

  const getOwnerOptions = () => {
    if (filters.ownerType === 'tenant') {
      return tenants.map(t => ({ value: t.id, label: t.name }));
    }
    if (filters.ownerType === 'user') {
      return users.map(u => ({ value: u.id, label: u.email || u.username }));
    }
    return [];
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <h1>🔑 Licenses</h1>
        <button className="btn btn-primary" onClick={() => setShowGenerateModal(true)}>
          + Generate Licenses
        </button>
      </div>

      {message && (
        <div className="alert alert-success">✅ {message}</div>
      )}
      {error && (
        <div className="alert alert-error">❌ {error}</div>
      )}

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="🔍 Search by license key..."
            value={filters.search}
            onChange={handleSearch}
          />
        </div>
        <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)}>
          <option value="all">All Statuses</option>
          {enums.license_status?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <select value={filters.ownerType} onChange={(e) => handleFilterChange('ownerType', e.target.value)}>
          <option value="">All Owner Types</option>
          {enums.license_owner_type?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        {filters.ownerType && (
          <select value={filters.owner} onChange={(e) => handleFilterChange('owner', e.target.value)}>
            <option value="">All {filters.ownerType === 'tenant' ? 'Tenants' : 'Users'}</option>
            {getOwnerOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* License Table */}
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th className="sortable" onClick={() => handleSort('license_key')}>
                    License Key {getSortIcon('license_key')}
                  </th>
                  <th className="sortable" onClick={() => handleSort('status')}>
                    Status {getSortIcon('status')}
                  </th>
                  <th>Owner Type</th>
                  <th>Owner</th>
                  <th className="sortable" onClick={() => handleSort('created_at')}>
                    Created {getSortIcon('created_at')}
                  </th>
                  <th>Device</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {licenses.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="empty-state">
                      No licenses found. <button onClick={() => setShowGenerateModal(true)} className="link-button">Generate licenses →</button>
                    </td>
                  </tr>
                ) : (
                  licenses.map((license) => (
                    <tr key={license.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.9rem', fontWeight: '500' }}>
                        {license.licenseKey}
                      </td>
                      <td>{getStatusBadge(license.status)}</td>
                      <td>{getOwnerTypeBadge(license.ownerType)}</td>
                      <td>{getOwnerName(license)}</td>
                      <td>{formatDate(license.createdAt)}</td>
                      <td>{getDeviceLink(license)}</td>
                      <td>
                        <div className="actions-dropdown">
                          <button className="btn btn-secondary btn-sm">
                            {actionLoading === license.id ? '...' : '⋮'}
                          </button>
                          <div className="dropdown-menu">
                            <Link to={`/licenses/${license.id}`} className="dropdown-item">
                              👁 View Details
                            </Link>
                            {license.status !== 'activated' && license.status !== 'revoked' && (
                              <button 
                                className="dropdown-item" 
                                onClick={() => setAssignLicense(license)}
                              >
                                🔗 Assign Owner
                              </button>
                            )}
                            {license.status !== 'revoked' && license.status !== 'deactivated' && (
                              <button 
                                className="dropdown-item text-danger" 
                                onClick={() => setRevokeLicense(license)}
                              >
                                ⛔ Revoke
                              </button>
                            )}
                            {license.status === 'revoked' && (
                              <button 
                                className="dropdown-item text-success" 
                                onClick={() => handleUnrevoke(license)}
                                disabled={actionLoading === license.id}
                              >
                                ✅ Unrevoke
                              </button>
                            )}
                            {license.status === 'activated' && (
                              <button 
                                className="dropdown-item text-warning" 
                                onClick={() => setDeactivateLicense(license)}
                              >
                                🔓 Deactivate
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalCount > 0 && (
            <div className="pagination">
              <span>Showing {((pagination.page - 1) * pagination.pageSize) + 1}-{Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of {pagination.totalCount} licenses</span>
              <div className="pagination-buttons">
                <button 
                  disabled={pagination.page === 1}
                  onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                >
                  ← Previous
                </button>
                <button 
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Generate License Modal */}
      {showGenerateModal && (
        <GenerateLicenseModal
          enums={enums}
          tenants={tenants}
          users={users}
          onClose={() => setShowGenerateModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowGenerateModal(false); fetchLicenses(); }}
          onError={setError}
        />
      )}

      {/* Assign Owner Modal */}
      {assignLicense && (
        <AssignOwnerModal
          license={assignLicense}
          enums={enums}
          tenants={tenants}
          users={users}
          onClose={() => setAssignLicense(null)}
          onSuccess={(msg) => { setMessage(msg); setAssignLicense(null); fetchLicenses(); }}
          onError={setError}
        />
      )}

      {/* Revoke Confirmation Modal */}
      {revokeLicense && (
        <RevokeConfirmModal
          license={revokeLicense}
          onClose={() => setRevokeLicense(null)}
          onConfirm={() => handleRevoke(revokeLicense)}
          loading={actionLoading === revokeLicense.id}
        />
      )}

      {/* Deactivate Confirmation Modal */}
      {deactivateLicense && (
        <DeactivateConfirmModal
          license={deactivateLicense}
          onClose={() => setDeactivateLicense(null)}
          onConfirm={() => handleDeactivate(deactivateLicense)}
          loading={actionLoading === deactivateLicense.id}
        />
      )}
    </div>
  );
};

export default Licenses;
